// src/App.js
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import ProjectWorkspaceShell from './components/ProjectWorkspaceShell';
import AppShell from './components/AppShell';

const isChunkLoadError = (error) => /loading chunk|chunkloaderror|failed to fetch dynamically imported module/i.test(String(error?.message || error || ''));

const lazyWithChunkRecovery = (importer) => lazy(() => importer().catch((error) => {
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
        const reloadKey = 'foerderbuddyChunkRecoveryReloaded';
        if (!window.sessionStorage.getItem(reloadKey)) {
            window.sessionStorage.setItem(reloadKey, '1');
            window.location.reload();
            return new Promise(() => {});
        }
    }
    throw error;
}));

const EditPage = lazyWithChunkRecovery(() => import('./pages/EditPage'));
const OverviewPage = lazyWithChunkRecovery(() => import('./pages/OverviewPage'));
const ProjectsList = lazyWithChunkRecovery(() => import('./pages/ProjectsList'));
const AbrechnungenPage = lazyWithChunkRecovery(() => import('./pages/BillingWorkspacePage'));
const WelcomePage = lazyWithChunkRecovery(() => import('./pages/WelcomePage'));
const ProjectDescriptionPage = lazyWithChunkRecovery(() => import('./pages/ProjectDescriptionPage'));
const ProjectAuditPage = lazyWithChunkRecovery(() => import('./pages/ProjectAuditPage'));
const FundingPotentialPage = lazyWithChunkRecovery(() => import('./pages/FundingPotentialPage'));
const ProjectCaseFilePage = lazyWithChunkRecovery(() => import('./pages/ProjectCaseFilePage'));
const ProjectEvidenceCenterPage = lazyWithChunkRecovery(() => import('./pages/ProjectEvidenceCenterPage'));
const InitialConsultationPage = lazyWithChunkRecovery(() => import('./pages/InitialConsultationPage'));
const CustomerPortalAdminPage = lazyWithChunkRecovery(() => import('./pages/CustomerPortalAdminPage'));
const CustomerPortalPage = lazyWithChunkRecovery(() => import('./pages/CustomerPortalPage'));
const ControllingPortalPage = lazyWithChunkRecovery(() => import('./pages/ControllingPortalPage'));
const ReviewerQuestionsPage = lazyWithChunkRecovery(() => import('./pages/ReviewerQuestionsPage'));
const SchemaHealthPage = lazyWithChunkRecovery(() => import('./pages/SchemaHealthPage'));
const WorkflowCockpitPage = lazyWithChunkRecovery(() => import('./pages/WorkflowCockpitPage'));
const CrmAccountsPage = lazyWithChunkRecovery(() => import('./pages/CrmAccountsPage'));
const RevenueEnginePage = lazyWithChunkRecovery(() => import('./pages/RevenueEnginePage'));
const FinanceFlowPage = lazyWithChunkRecovery(() => import('./pages/FinanceFlowPage'));
const FundingDatabaseOptimizer = lazyWithChunkRecovery(() => import('./pages/FundingDatabaseOptimizer'));
const AiConfigurationPage = lazyWithChunkRecovery(() => import('./pages/AiConfigurationPage'));
const OfferTemplatesPage = lazyWithChunkRecovery(() => import('./pages/OfferTemplatesPage'));
const ProjectDashboard = lazyWithChunkRecovery(() => import('./pages/ProjectDashboard'));

const AUTH_TOKEN_STORAGE_KEY = 'foerderbuddyAuthToken';

const getStoredAuthToken = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '';
};

const INITIAL_AUTH_TOKEN = getStoredAuthToken();

const applyAuthToken = (token) => {
    if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
        }
        return;
    }

    delete axios.defaults.headers.common.Authorization;
    if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
};

applyAuthToken(INITIAL_AUTH_TOKEN);

const isProtectedApiRequest = (url = '') => (
    /^\/api\/(?:airtable|openai|ai-training|workflow|funding-database|revenue-engine|finance|crm|integrations)(?:\/|$)/.test(String(url || ''))
);

function App() {
    const [authToken, setAuthToken] = useState(INITIAL_AUTH_TOKEN);
    const isAuthenticated = Boolean(authToken);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            window.sessionStorage.removeItem('foerderbuddyChunkRecoveryReloaded');
        }, 3000);

        return () => window.clearTimeout(timer);
    }, []);

    useEffect(() => {
        applyAuthToken(authToken);
    }, [authToken]);

    useEffect(() => {
        const interceptorId = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const requestUrl = error.config?.url || '';
                if (error.response?.status === 401 && isProtectedApiRequest(requestUrl)) {
                    setAuthToken('');
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    const withProjectWorkspace = (activeSection, element) => (
        <ProjectWorkspaceShell activeSection={activeSection}>
            {element}
        </ProjectWorkspaceShell>
    );

    const withAppShell = (element) => (
        <AppShell>{element}</AppShell>
    );
    
    const authenticate = async (password) => {
        try {
            const response = await axios.post('/api/auth/login', { password });
            if (response.data.authenticated && response.data.token) {
                const nextAuthToken = response.data.token;
                applyAuthToken(nextAuthToken);
                setAuthToken(nextAuthToken);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Authentication failed:', error);
            return false;
        }
    };

    return (
        <ErrorBoundary>
            <Router>
                <Suspense fallback={<div className="app-route-loading">Lade...</div>}>
                    <Routes>
                        <Route path="/" element={isAuthenticated ? <Navigate to="/workflow" replace /> : <WelcomePage authenticate={authenticate} />} />
                        <Route path="/admin" element={<PrivateRoute isAuthenticated={isAuthenticated}><Navigate to="/workflow" replace /></PrivateRoute>} />
                        <Route path="/workflow" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<WorkflowCockpitPage />)}</PrivateRoute>} />
                        <Route path="/crm" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<CrmAccountsPage />)}</PrivateRoute>} />
                        <Route path="/revenue" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<RevenueEnginePage />)}</PrivateRoute>} />
                        <Route path="/finance" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<FinanceFlowPage />)}</PrivateRoute>} />
                        <Route path="/projects" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<ProjectsList />)}</PrivateRoute>} />
                        <Route path="/schema-health" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<SchemaHealthPage />)}</PrivateRoute>} />
                        <Route path="/funding-database" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<FundingDatabaseOptimizer />)}</PrivateRoute>} />
                        <Route path="/ai-configuration" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<AiConfigurationPage />)}</PrivateRoute>} />
                        <Route path="/offer-templates" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withAppShell(<OfferTemplatesPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/dashboard" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('dashboard', <ProjectDashboard />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/case-file" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('case-file', <ProjectCaseFilePage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/evidence" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('evidence', <ProjectEvidenceCenterPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/edit" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('edit', <EditPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/overview" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('overview', <OverviewPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/abrechnungen" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('abrechnungen', <AbrechnungenPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/description" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('description', <ProjectDescriptionPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/initial-consultation" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('initial-consultation', <InitialConsultationPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/funding-potential" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('funding-potential', <FundingPotentialPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/audit" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('audit', <ProjectAuditPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/customer-portal" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('customer-portal', <CustomerPortalAdminPage />)}</PrivateRoute>} />
                        <Route path="/project/:projectId/reviewer-questions" element={<PrivateRoute isAuthenticated={isAuthenticated}>{withProjectWorkspace('reviewer-questions', <ReviewerQuestionsPage />)}</PrivateRoute>} />
                        <Route path="/portal/:token" element={<CustomerPortalPage />} />
                        <Route path="/controlling-portal/:token" element={<ControllingPortalPage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                </Suspense>
            </Router>
        </ErrorBoundary>
    );
}

export default App;
